'use client';

// Dormitory-mode pages (per-plant add-on). Rooms/tenants/rates live in
// config_data.dorm (persisted via store.saveDormConfig); electric usage is read
// live from each room's linked device (energy-usage monthly bucket).
// Bilingual (TH/EN via store.lang) + premium styling.

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useAppStore } from '@/stores/app-store';
import type { DormConfig, DormRoom, DormTenant, Plant, Lang } from '@/lib/types';

// ── palette (theme-aware tokens) ──
const C = {
  surface: 'var(--bg-surface)', elevated: 'var(--bg-elevated)', base: 'var(--bg-base)', border: 'var(--border)',
  borderSub: 'var(--border-subtle)', text: 'var(--text-primary)', text2: 'var(--text-secondary)',
  muted: 'var(--text-muted)', accent: 'var(--accent)', green: 'var(--green)', red: 'var(--red)',
  amber: 'var(--amber)', cyan: 'var(--cyan)', blue: 'var(--blue)', purple: 'var(--purple)',
};
const card: CSSProperties = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 18, boxShadow: '0 1px 2px rgba(0,0,0,.04), 0 20px 44px -28px rgba(0,0,0,.4)', overflow: 'hidden' };
const inputCss: CSSProperties = { width: '100%', padding: '9px 11px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.elevated, color: C.text, fontSize: 13, fontFamily: 'inherit', outline: 'none' };
const thCss: CSSProperties = { textAlign: 'left', padding: '11px 16px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: C.muted, borderBottom: `1px solid ${C.border}`, background: `color-mix(in srgb, var(--text-muted) 5%, var(--bg-surface))`, whiteSpace: 'nowrap', position: 'sticky', top: 0 };
const tdCss: CSSProperties = { padding: '11px 16px', fontSize: 13, color: C.text2, borderBottom: `1px solid ${C.borderSub}`, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

// ── bilingual dictionary [th, en] ──
const TXT: Record<string, [string, string]> = {
  dormMode: ['โหมดหอพัก', 'Dorm mode'],
  overview: ['ภาพรวมหอพัก', 'Dorm Overview'],
  rooms: ['ห้อง', 'Rooms'], tenants: ['ผู้เช่า', 'Tenants'], meter: ['จดมิเตอร์', 'Meter reading'],
  billing: ['บิล & ชำระเงิน', 'Billing'],
  selectDorm: ['เลือกหอพัก (plant) ก่อน', 'Select a dorm plant first'],
  // overview
  totalRooms: ['ห้องทั้งหมด', 'Total rooms'], occupied: ['มีผู้เช่า', 'Occupied'], vacant: ['ห้องว่าง', 'Vacant'],
  incomeEst: ['รายได้เดือนนี้ (คาด)', 'Est. income (month)'], totalKwh: ['หน่วยไฟรวม', 'Total kWh'],
  readyToRent: ['พร้อมปล่อยเช่า', 'ready to rent'], occRate: ['อัตราเข้าพัก', 'occupancy'],
  rentElecCommon: ['ค่าเช่า+ไฟ+ส่วนกลาง', 'rent + power + fee'], fromIoT: ['จาก IoT', 'from IoT meters'],
  roomMap: ['ผังห้อง · สถานะ', 'Room map · status'], roomsUnit: ['ห้อง', 'rooms'],
  stOcc: ['เช่าอยู่', 'Occupied'], stVac: ['ว่าง', 'Vacant'], noRoomsAdd: ["ยังไม่มีห้อง — เพิ่มที่เมนู 'ห้อง'", "No rooms yet — add from the Rooms menu"],
  // rooms
  addRoom: ['+ เพิ่มห้อง', '+ Add room'], allRooms: ['ห้องทั้งหมด', 'All rooms'],
  linkHint: ['ผูกห้อง ↔ มิเตอร์ (device) ของไซต์นี้', 'Link a room to a meter device on this site'],
  roomNo: ['เลขห้อง', 'Room no'], floor: ['ชั้น', 'Floor'], type: ['ประเภท', 'Type'], rent: ['ค่าเช่า/เดือน (บาท)', 'Rent/month (฿)'],
  meterElec: ['มิเตอร์ไฟ (device)', 'Electric meter (device)'], noMeter: ['— จดมือ (ไม่มี IoT) —', '— Manual (no IoT) —'],
  editRoomT: ['แก้ไขห้อง', 'Edit room'], addRoomT: ['เพิ่มห้อง', 'Add room'],
  colTenant: ['ผู้เช่า', 'Tenant'], colMeter: ['มิเตอร์ไฟ', 'Meter'], colRent: ['ค่าเช่า', 'Rent'],
  vacantDash: ['— ว่าง —', '— vacant —'], manual: ['จดมือ', 'Manual'], edit: ['แก้ไข', 'Edit'], del: ['ลบ', 'Delete'],
  save: ['บันทึก', 'Save'], saving: ['กำลังบันทึก…', 'Saving…'], cancel: ['ยกเลิก', 'Cancel'],
  noRooms: ["ยังไม่มีห้อง — กด '+ เพิ่มห้อง'", "No rooms — click '+ Add room'"], confirmDelRoom: ['ลบห้องนี้?', 'Delete this room?'],
  // tenants
  addTenant: ['+ เพิ่มผู้เช่า', '+ Add tenant'], allTenants: ['ผู้เช่าทั้งหมด', 'All tenants'],
  name: ['ชื่อ', 'Name'], phone: ['เบอร์โทร', 'Phone'], room: ['ห้อง', 'Room'], moveIn: ['วันเข้าอยู่', 'Move-in'],
  contractEnd: ['สัญญาถึง', 'Contract end'], deposit: ['เงินมัดจำ (บาท)', 'Deposit (฿)'], unassigned: ['— ยังไม่กำหนด —', '— Unassigned —'],
  editTenantT: ['แก้ไขผู้เช่า', 'Edit tenant'], addTenantT: ['เพิ่มผู้เช่า', 'Add tenant'], contact: ['ติดต่อ', 'Contact'],
  noTenants: ["ยังไม่มีผู้เช่า — กด '+ เพิ่มผู้เช่า'", "No tenants — click '+ Add tenant'"], confirmDelTen: ['ลบผู้เช่านี้?', 'Delete this tenant?'],
  // meter
  meterBanner: ['ห้องที่มี IoT meter ระบบดึงหน่วยไฟอัตโนมัติจาก telemetry · ห้องที่ยังไม่มี IoT ใช้จดมือ (เฟสถัดไป)', 'IoT-metered rooms are read automatically from telemetry · manual entry for non-IoT rooms (next phase)'],
  unitsThisMonth: ['หน่วยใช้', 'Usage'], unitsCol: ['หน่วยไฟ (kWh)', 'Units (kWh)'], source: ['แหล่ง', 'Source'],
  usageEq: ['หน่วยใช้ = สิ้นสุด − เริ่ม', 'Usage = end − start'], anomaly: ['⚠ ค่ากระโดด', '⚠ spike'],
  // bill
  genAll: ['🧾 ออกบิลทั้งหอ', '🧾 Generate all bills'],
  rateNote: ['ตั้งค่าเรตได้ที่ตั้งค่าไซต์ (เฟสถัดไป)', 'rates configurable in Site Settings (next phase)'],
  monthlyBills: ['บิลรายเดือน', 'Monthly bills'], total: ['รวม', 'Total'], unitsShort: ['หน่วยไฟ', 'Units'],
  elecCost: ['ค่าไฟ', 'Power'], common: ['ส่วนกลาง', 'Common'], grand: ['รวม', 'Total'],
  noBillRooms: ['ยังไม่มีห้องที่มีผู้เช่า', 'No occupied rooms yet'],
  billFoot: ['* ค่าไฟ IoT อัตโนมัติ · ค่าน้ำ+บันทึกชำระใช้ได้แล้ว · พิมพ์ PDF / ส่ง LINE จะทำเฟสถัดไป', '* Power auto-read from IoT · water + payment tracking are live · PDF / LINE send coming next'],
  ratePrefix: ['เรตค่าไฟ', 'Power'], perUnit: ['/หน่วย', '/unit'], water: ['ค่าน้ำ', 'Water'],
  // phase 2
  elecUnits: ['หน่วยไฟ (kWh)', 'Electric (kWh)'], waterUnits: ['หน่วยน้ำ (m³)', 'Water (m³)'],
  saveReading: ['บันทึกหน่วย', 'Save readings'], autoFilled: ['เติมจาก IoT', 'auto from IoT'],
  editRates: ['⚙ ตั้งค่าเรต', '⚙ Edit rates'], saveRates: ['บันทึกเรต', 'Save rates'],
  elecRateL: ['ค่าไฟ (บาท/หน่วย)', 'Power rate (฿/unit)'], waterRateL: ['ค่าน้ำ (บาท/หน่วย)', 'Water rate (฿/unit)'], commonFeeL: ['ค่าส่วนกลาง (บาท)', 'Common fee (฿)'],
  waterCol: ['ค่าน้ำ', 'Water'], status: ['สถานะ', 'Status'], paid: ['ชำระแล้ว', 'Paid'], unpaid: ['ค้างชำระ', 'Unpaid'],
  markPaid: ['บันทึกชำระ', 'Mark paid'], markUnpaid: ['ยกเลิกชำระ', 'Unmark'],
  paidSum: ['เก็บได้แล้ว', 'Collected'], unpaidSum: ['ค้างชำระ', 'Outstanding'],
  readHint: ['กรอกเลขหน่วย (ไฟ/น้ำ) ของเดือนนี้ แล้วกดบันทึก · ห้อง IoT เติมค่าไฟให้อัตโนมัติ (แก้ได้)', 'Enter this month’s electric/water units then save · IoT rooms auto-fill power (editable)'],
  // phase 3
  overdue: ['เกินกำหนด', 'Overdue'], dueDay: ['วันครบกำหนด', 'Due day'], due: ['ครบกำหนด', 'due'],
  printBill: ['พิมพ์บิล', 'Print'], printAll: ['🖨 พิมพ์บิลทั้งหมด', '🖨 Print all'], sendLine: ['ส่ง LINE', 'LINE'],
  invoiceTitle: ['ใบแจ้งค่าเช่า', 'Rent Invoice'], billFor: ['ประจำเดือน', 'For'],
};

const money = (n: number) => '฿' + Math.round(n).toLocaleString();
const uid = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'r' + Math.random().toString(36).slice(2));

// ── small UI atoms ──
function Btn({ children, onClick, variant = 'ghost', small }: { children: ReactNode; onClick?: () => void; variant?: 'primary' | 'ghost' | 'danger'; small?: boolean }) {
  const base: CSSProperties = { fontFamily: 'inherit', fontSize: small ? 12 : 13, fontWeight: 700, borderRadius: 10, padding: small ? '5px 11px' : '9px 15px', cursor: 'pointer', border: `1px solid ${C.border}`, background: C.elevated, color: C.text, transition: 'all .15s', whiteSpace: 'nowrap' };
  const v = variant === 'primary' ? { background: `linear-gradient(135deg, var(--cyan), var(--accent))`, border: 'none', color: '#04211d', boxShadow: '0 6px 16px -8px color-mix(in srgb, var(--accent) 70%, transparent)' }
    : variant === 'danger' ? { color: C.red, borderColor: 'color-mix(in srgb, var(--red) 40%, transparent)', background: 'color-mix(in srgb, var(--red) 10%, transparent)' } : {};
  return <button style={{ ...base, ...v }} onClick={onClick}>{children}</button>;
}
function Card({ title, extra, children }: { title: string; extra?: ReactNode; children: ReactNode }) {
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '15px 18px 11px', flexWrap: 'wrap', borderBottom: `1px solid ${C.borderSub}` }}>
        <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 800, color: C.text, letterSpacing: '-.01em' }}>{title}</h2>
        {extra}
      </div>
      {children}
    </div>
  );
}
function Header({ plant, title, lang, right }: { plant: Plant; title: string; lang: Lang; right?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
        <span style={{ width: 46, height: 46, borderRadius: 14, background: 'linear-gradient(135deg, var(--cyan), var(--accent))', display: 'grid', placeItems: 'center', fontSize: 22, boxShadow: '0 8px 20px -8px color-mix(in srgb, var(--accent) 65%, transparent)' }}>🏠</span>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: '-.02em', color: C.text }}>{title}</h1>
          <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: C.accent, background: 'color-mix(in srgb, var(--accent) 13%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)', padding: '2px 9px', borderRadius: 999 }}>🏠 {TXT.dormMode[lang === 'th' ? 0 : 1]}</span>
            <span style={{ fontSize: 12, color: C.muted }}>{plant.name}</span>
          </div>
        </div>
      </div>
      {right}
    </div>
  );
}
function Tag({ children, c }: { children: ReactNode; c: string }) {
  return <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 9px', borderRadius: 999, color: c, background: `color-mix(in srgb, ${c} 15%, transparent)`, whiteSpace: 'nowrap' }}>{children}</span>;
}
function Hint({ text }: { text: string }) { return <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: C.muted }}>{text}</div>; }
function Empty({ text }: { text: string }) { return <div style={{ padding: 48, textAlign: 'center', color: C.muted }}>{text}</div>; }
function L({ label, children }: { label: string; children: ReactNode }) {
  return <label style={{ display: 'block' }}><span style={{ display: 'block', fontSize: 11, color: C.muted, marginBottom: 5, fontWeight: 600 }}>{label}</span>{children}</label>;
}

// ── bill export helpers (print → PDF, LINE share) ──
interface BillLine { room: string; tenant: string; rent: number; elec: number; elecU: number; water: number; waterU: number; total: number }
const tk = (lang: Lang, k: string) => TXT[k]?.[lang === 'th' ? 0 : 1] ?? k;
function printBills(bills: BillLine[], plantName: string, rates: { elecRate: number; waterRate: number; commonFee: number }, monthLbl: string, dueStr: string, lang: Lang) {
  const t = (k: string) => tk(lang, k);
  const unit = lang === 'th' ? 'หน่วย' : 'units';
  const inv = bills.map((b) => `
    <div class="inv">
      <div class="t">${t('invoiceTitle')} · ${t('room')} ${b.room}</div>
      <div class="s">${plantName} · ${t('billFor')} ${monthLbl} · ${t('due')} ${dueStr}</div>
      <table>
        <tr><td>${t('colRent')}</td><td class="r">฿${b.rent.toLocaleString()}</td></tr>
        <tr><td>${t('elecCost')} · ${b.elecU} ${unit} × ฿${rates.elecRate}</td><td class="r">฿${Math.round(b.elec).toLocaleString()}</td></tr>
        <tr><td>${t('water')} · ${b.waterU} ${unit} × ฿${rates.waterRate}</td><td class="r">฿${Math.round(b.water).toLocaleString()}</td></tr>
        <tr><td>${t('common')}</td><td class="r">฿${rates.commonFee.toLocaleString()}</td></tr>
        <tr class="tot"><td>${t('grand')}</td><td class="r">฿${Math.round(b.total).toLocaleString()}</td></tr>
      </table>
      <div class="who">${b.tenant}</div>
    </div>`).join('');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${t('invoiceTitle')}</title><style>
    body{font-family:'Noto Sans Thai','Plus Jakarta Sans',system-ui,sans-serif;padding:22px;color:#141414;background:#fff}
    .inv{max-width:430px;border:1px solid #d7d7d7;border-radius:12px;padding:18px;margin:0 auto 20px;page-break-inside:avoid}
    .t{font-weight:800;font-size:17px}.s{color:#666;font-size:12px;margin:3px 0 12px}
    table{width:100%;border-collapse:collapse;font-size:13px}
    td{padding:7px 0;border-bottom:1px dashed #ececec}.r{text-align:right;font-weight:600}
    .tot td{border-top:2px solid #222;border-bottom:none;font-weight:800;font-size:16px;padding-top:9px}
    .who{margin-top:10px;font-size:12px;color:#555}
  </style></head><body>${inv}<script>window.onload=function(){window.print()}</script></body></html>`;
  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); }
}
function lineShareBill(b: BillLine, plantName: string, monthLbl: string, rates: { commonFee: number }, lang: Lang) {
  const t = (k: string) => tk(lang, k);
  const txt = `${t('invoiceTitle')} · ${t('room')} ${b.room}\n${plantName} · ${monthLbl}\n${t('colRent')}: ฿${b.rent.toLocaleString()}\n${t('elecCost')}: ฿${Math.round(b.elec).toLocaleString()}\n${t('water')}: ฿${Math.round(b.water).toLocaleString()}\n${t('common')}: ฿${rates.commonFee.toLocaleString()}\n${t('grand')}: ฿${Math.round(b.total).toLocaleString()}`;
  window.open('https://line.me/R/msg/text/?' + encodeURIComponent(txt), '_blank');
}

// ── shared hooks ──
function useDorm() {
  const currentPlant = useAppStore((s) => s.currentPlant);
  const plant = useAppStore((s) => (currentPlant ? s.plants[currentPlant] : null));
  const saveDormConfig = useAppStore((s) => s.saveDormConfig);
  const lang = useAppStore((s) => s.lang);
  const dorm: DormConfig = plant?.dorm ?? {};
  const rooms = dorm.rooms ?? [];
  const tenants = dorm.tenants ?? [];
  const rates = { elecRate: dorm.elecRate ?? 7, waterRate: dorm.waterRate ?? 18, commonFee: dorm.commonFee ?? 0 };
  const tr = (k: string) => TXT[k]?.[lang === 'th' ? 0 : 1] ?? k;
  const save = async (patch: Partial<DormConfig>) => { if (plant) await saveDormConfig(plant.id, { ...dorm, ...patch }); };
  return { plant, dorm, rooms, tenants, rates, lang, tr, save };
}
function useMonthlyUsage(rooms: DormRoom[]) {
  const [usage, setUsage] = useState<Record<string, { units: number | null; anomaly?: boolean }>>({});
  const key = rooms.map((r) => `${r.id}:${r.meterSn ?? ''}`).join('|');
  useEffect(() => {
    let alive = true;
    const now = new Date();
    const mk = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    (async () => {
      const out: Record<string, { units: number | null; anomaly?: boolean }> = {};
      await Promise.all(rooms.filter((r) => r.meterSn).map(async (r) => {
        try {
          const res = await fetch(`/api/devices/${encodeURIComponent(r.meterSn as string)}/energy-usage?bucket=month&range=90d&key=kwh`);
          const d = await res.json();
          const row = (d.rows || []).find((x: { bucketKey?: string }) => x.bucketKey === mk) ?? (d.rows || [])[0];
          out[r.id] = { units: row?.usage ?? null, anomaly: row?.anomaly };
        } catch { out[r.id] = { units: null }; }
      }));
      if (alive) setUsage(out);
    })();
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return usage;
}
const monthLabel = (lang: Lang) => new Date().toLocaleDateString(lang === 'th' ? 'th-TH' : 'en-US', { month: 'long', year: 'numeric' });
const monthKey = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; };
const numInput: CSSProperties = { width: 92, padding: '6px 9px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.elevated, color: C.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', textAlign: 'right' };

// ════════════════════════════ OVERVIEW ════════════════════════════
export function DormOverviewPage() {
  const { plant, rooms, tenants, rates, lang, tr } = useDorm();
  const usage = useMonthlyUsage(rooms);
  if (!plant) return <Empty text={tr('selectDorm')} />;
  const tenantByRoom = new Map(tenants.filter((t) => t.roomId).map((t) => [t.roomId!, t]));
  const occ = rooms.filter((r) => tenantByRoom.has(r.id)).length;
  const income = rooms.reduce((s, r) => tenantByRoom.has(r.id) ? s + (r.rent ?? 0) + (usage[r.id]?.units ?? 0) * rates.elecRate + rates.commonFee : s, 0);
  const totalKwh = rooms.reduce((s, r) => s + (usage[r.id]?.units ?? 0), 0);
  const kpis = [
    { lab: tr('totalRooms'), val: String(rooms.length), sub: '', c: C.accent },
    { lab: tr('occupied'), val: `${occ}/${rooms.length}`, sub: rooms.length ? `${tr('occRate')} ${Math.round((occ / rooms.length) * 100)}%` : '', c: C.green },
    { lab: tr('vacant'), val: String(rooms.length - occ), sub: tr('readyToRent'), c: C.muted },
    { lab: tr('incomeEst'), val: money(income), sub: tr('rentElecCommon'), c: C.blue },
    { lab: tr('totalKwh'), val: Math.round(totalKwh).toLocaleString() + ' kWh', sub: tr('fromIoT'), c: C.amber },
  ];
  return (
    <div style={{ padding: '4px 2px' }}>
      <Header plant={plant} title={tr('overview')} lang={lang} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(158px,1fr))', gap: 13, marginBottom: 16 }}>
        {kpis.map((k) => (
          <div key={k.lab} style={{ ...card, padding: '15px 17px', position: 'relative', background: `linear-gradient(135deg, color-mix(in srgb, ${k.c} 8%, var(--bg-surface)), var(--bg-surface))` }}>
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: k.c }} />
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: C.muted }}>{k.lab}</div>
            <div style={{ marginTop: 7, fontSize: 24, fontWeight: 800, color: C.text, lineHeight: 1, letterSpacing: '-.02em' }}>{k.val}</div>
            {k.sub && <div style={{ marginTop: 6, fontSize: 11, color: C.text2 }}>{k.sub}</div>}
          </div>
        ))}
      </div>
      <Card title={tr('roomMap')} extra={<span style={{ fontSize: 11.5, color: C.muted }}>{rooms.length} {tr('roomsUnit')}</span>}>
        {rooms.length === 0 ? <Hint text={tr('noRoomsAdd')} /> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px,1fr))', gap: 9, padding: '14px 18px 18px' }}>
            {rooms.map((r) => {
              const o = tenantByRoom.has(r.id);
              return (
                <div key={r.id} title={o ? tenantByRoom.get(r.id)!.name : tr('stVac')} style={{ borderRadius: 12, padding: '11px 6px', textAlign: 'center', border: `1px solid ${o ? 'color-mix(in srgb, var(--green) 42%, transparent)' : C.border}`, background: o ? 'color-mix(in srgb, var(--green) 12%, var(--bg-surface))' : C.elevated, borderStyle: o ? 'solid' : 'dashed', transition: 'transform .12s' }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{r.no}</div>
                  <div style={{ fontSize: 9, fontWeight: 700, marginTop: 3, color: o ? C.green : C.muted }}>{o ? tr('stOcc') : tr('stVac')}</div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

// ════════════════════════════ ROOMS ════════════════════════════
const emptyRoom = (): DormRoom => ({ id: uid(), no: '', floor: '', roomType: '', rent: 0, meterSn: null });
export function DormRoomsPage() {
  const { plant, rooms, tenants, lang, tr, save } = useDorm();
  const [editing, setEditing] = useState<DormRoom | null>(null);
  const [busy, setBusy] = useState(false);
  if (!plant) return <Empty text={tr('selectDorm')} />;
  const devices = plant.devices ?? [];
  const tenantByRoom = new Map(tenants.filter((t) => t.roomId).map((t) => [t.roomId!, t]));
  const commit = async (next: DormRoom[]) => { setBusy(true); await save({ rooms: next }); setBusy(false); };
  const onSave = async () => {
    if (!editing || !editing.no.trim()) return;
    const exists = rooms.some((r) => r.id === editing.id);
    await commit(exists ? rooms.map((r) => (r.id === editing.id ? editing : r)) : [...rooms, editing]);
    setEditing(null);
  };
  const onDelete = async (id: string) => { if (confirm(tr('confirmDelRoom'))) await commit(rooms.filter((r) => r.id !== id)); };
  return (
    <div style={{ padding: '4px 2px' }}>
      <Header plant={plant} title={tr('rooms')} lang={lang} right={<Btn variant="primary" onClick={() => setEditing(emptyRoom())}>{tr('addRoom')}</Btn>} />
      {editing && (
        <div style={{ ...card, padding: 17, marginBottom: 15 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, marginBottom: 12 }}>{rooms.some((r) => r.id === editing.id) ? tr('editRoomT') : tr('addRoomT')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px,1fr))', gap: 11 }}>
            <L label={tr('roomNo') + ' *'}><input style={inputCss} value={editing.no} onChange={(e) => setEditing({ ...editing, no: e.target.value })} placeholder="101" /></L>
            <L label={tr('floor')}><input style={inputCss} value={editing.floor ?? ''} onChange={(e) => setEditing({ ...editing, floor: e.target.value })} placeholder="1" /></L>
            <L label={tr('type')}><input style={inputCss} value={editing.roomType ?? ''} onChange={(e) => setEditing({ ...editing, roomType: e.target.value })} placeholder={lang === 'th' ? 'สตูดิโอ' : 'Studio'} /></L>
            <L label={tr('rent')}><input type="number" style={inputCss} value={editing.rent ?? 0} onChange={(e) => setEditing({ ...editing, rent: Number(e.target.value) })} /></L>
            <L label={tr('meterElec')}>
              <select style={inputCss} value={editing.meterSn ?? ''} onChange={(e) => setEditing({ ...editing, meterSn: e.target.value || null })}>
                <option value="">{tr('noMeter')}</option>
                {devices.map((d) => <option key={d.sn} value={d.sn}>{d.name || d.sn}</option>)}
              </select>
            </L>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 13 }}>
            <Btn variant="primary" onClick={onSave}>{busy ? tr('saving') : tr('save')}</Btn>
            <Btn onClick={() => setEditing(null)}>{tr('cancel')}</Btn>
          </div>
        </div>
      )}
      <Card title={tr('allRooms')} extra={<span style={{ fontSize: 11.5, color: C.muted }}>{tr('linkHint')}</span>}>
        {rooms.length === 0 ? <Hint text={tr('noRooms')} /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead><tr><th style={thCss}>{tr('roomNo')}</th><th style={thCss}>{tr('floor')}</th><th style={thCss}>{tr('type')}</th><th style={thCss}>{tr('colTenant')}</th><th style={thCss}>{tr('colMeter')}</th><th style={{ ...thCss, textAlign: 'right' }}>{tr('colRent')}</th><th style={{ ...thCss, textAlign: 'right' }} /></tr></thead>
              <tbody>
                {rooms.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...tdCss, fontWeight: 800, color: C.text }}>{r.no}</td>
                    <td style={tdCss}>{r.floor || '—'}</td>
                    <td style={tdCss}>{r.roomType || '—'}</td>
                    <td style={tdCss}>{tenantByRoom.get(r.id)?.name ?? <span style={{ color: C.muted }}>{tr('vacantDash')}</span>}</td>
                    <td style={tdCss}>{r.meterSn ? <Tag c={C.blue}>⚡ {devices.find((d) => d.sn === r.meterSn)?.name || r.meterSn}</Tag> : <Tag c={C.amber}>✎ {tr('manual')}</Tag>}</td>
                    <td style={{ ...tdCss, textAlign: 'right', fontWeight: 700, color: C.text }}>{money(r.rent ?? 0)}</td>
                    <td style={{ ...tdCss, textAlign: 'right' }}><Btn small onClick={() => setEditing(r)}>{tr('edit')}</Btn> <Btn small variant="danger" onClick={() => onDelete(r.id)}>{tr('del')}</Btn></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ════════════════════════════ TENANTS ════════════════════════════
const emptyTenant = (): DormTenant => ({ id: uid(), name: '', phone: '', line: '', roomId: null, moveIn: '', contractEnd: '', deposit: 0 });
export function DormTenantsPage() {
  const { plant, rooms, tenants, lang, tr, save } = useDorm();
  const [editing, setEditing] = useState<DormTenant | null>(null);
  const [busy, setBusy] = useState(false);
  if (!plant) return <Empty text={tr('selectDorm')} />;
  const roomNo = (id?: string | null) => rooms.find((r) => r.id === id)?.no ?? '—';
  const commit = async (next: DormTenant[]) => { setBusy(true); await save({ tenants: next }); setBusy(false); };
  const onSave = async () => {
    if (!editing || !editing.name.trim()) return;
    const exists = tenants.some((t) => t.id === editing.id);
    await commit(exists ? tenants.map((t) => (t.id === editing.id ? editing : t)) : [...tenants, editing]);
    setEditing(null);
  };
  const onDelete = async (id: string) => { if (confirm(tr('confirmDelTen'))) await commit(tenants.filter((t) => t.id !== id)); };
  return (
    <div style={{ padding: '4px 2px' }}>
      <Header plant={plant} title={tr('tenants')} lang={lang} right={<Btn variant="primary" onClick={() => setEditing(emptyTenant())}>{tr('addTenant')}</Btn>} />
      {editing && (
        <div style={{ ...card, padding: 17, marginBottom: 15 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, marginBottom: 12 }}>{tenants.some((t) => t.id === editing.id) ? tr('editTenantT') : tr('addTenantT')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px,1fr))', gap: 11 }}>
            <L label={tr('name') + ' *'}><input style={inputCss} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></L>
            <L label={tr('phone')}><input style={inputCss} value={editing.phone ?? ''} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></L>
            <L label="LINE"><input style={inputCss} value={editing.line ?? ''} onChange={(e) => setEditing({ ...editing, line: e.target.value })} /></L>
            <L label={tr('room')}>
              <select style={inputCss} value={editing.roomId ?? ''} onChange={(e) => setEditing({ ...editing, roomId: e.target.value || null })}>
                <option value="">{tr('unassigned')}</option>
                {rooms.map((r) => <option key={r.id} value={r.id}>{r.no}</option>)}
              </select>
            </L>
            <L label={tr('moveIn')}><input type="date" style={inputCss} value={editing.moveIn ?? ''} onChange={(e) => setEditing({ ...editing, moveIn: e.target.value })} /></L>
            <L label={tr('contractEnd')}><input type="date" style={inputCss} value={editing.contractEnd ?? ''} onChange={(e) => setEditing({ ...editing, contractEnd: e.target.value })} /></L>
            <L label={tr('deposit')}><input type="number" style={inputCss} value={editing.deposit ?? 0} onChange={(e) => setEditing({ ...editing, deposit: Number(e.target.value) })} /></L>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 13 }}>
            <Btn variant="primary" onClick={onSave}>{busy ? tr('saving') : tr('save')}</Btn>
            <Btn onClick={() => setEditing(null)}>{tr('cancel')}</Btn>
          </div>
        </div>
      )}
      <Card title={tr('allTenants')}>
        {tenants.length === 0 ? <Hint text={tr('noTenants')} /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead><tr><th style={thCss}>{tr('colTenant')}</th><th style={thCss}>{tr('room')}</th><th style={thCss}>{tr('contact')}</th><th style={thCss}>{tr('moveIn')}</th><th style={thCss}>{tr('contractEnd')}</th><th style={{ ...thCss, textAlign: 'right' }}>{tr('deposit')}</th><th style={{ ...thCss, textAlign: 'right' }} /></tr></thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id}>
                    <td style={{ ...tdCss, fontWeight: 700, color: C.text }}>{t.name}</td>
                    <td style={tdCss}>{roomNo(t.roomId)}</td>
                    <td style={tdCss}>{[t.phone, t.line ? `LINE ${t.line}` : ''].filter(Boolean).join(' · ') || '—'}</td>
                    <td style={tdCss}>{t.moveIn || '—'}</td>
                    <td style={tdCss}>{t.contractEnd || '—'}</td>
                    <td style={{ ...tdCss, textAlign: 'right' }}>{money(t.deposit ?? 0)}</td>
                    <td style={{ ...tdCss, textAlign: 'right' }}><Btn small onClick={() => setEditing(t)}>{tr('edit')}</Btn> <Btn small variant="danger" onClick={() => onDelete(t.id)}>{tr('del')}</Btn></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ════════════════════════════ METER ════════════════════════════
export function DormMeterPage() {
  const { plant, dorm, rooms, lang, tr, save } = useDorm();
  const usage = useMonthlyUsage(rooms);
  const mk = monthKey();
  const saved = dorm.readings?.[mk] ?? {};
  const [edited, setEdited] = useState<Record<string, { elec: string; water: string }>>({});
  const [busy, setBusy] = useState(false);
  // init from saved readings
  useEffect(() => {
    const init: Record<string, { elec: string; water: string }> = {};
    for (const r of rooms) init[r.id] = { elec: saved[r.id]?.elec != null ? String(saved[r.id]!.elec) : '', water: saved[r.id]?.water != null ? String(saved[r.id]!.water) : '' };
    setEdited(init);
  }, [rooms.length, mk]); // eslint-disable-line react-hooks/exhaustive-deps
  // auto-fill IoT electric when telemetry arrives and no manual value yet
  useEffect(() => {
    setEdited((prev) => {
      const next = { ...prev };
      for (const r of rooms) {
        if (r.meterSn && next[r.id] && next[r.id].elec === '' && saved[r.id]?.elec == null) {
          const u = usage[r.id]?.units;
          if (u != null) next[r.id] = { ...next[r.id], elec: String(Math.round(u)) };
        }
      }
      return next;
    });
  }, [usage]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!plant) return <Empty text={tr('selectDorm')} />;
  const setVal = (id: string, k: 'elec' | 'water', v: string) => setEdited((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));
  const onSave = async () => {
    setBusy(true);
    const monthReadings: Record<string, { elec?: number; water?: number }> = {};
    for (const r of rooms) {
      const e = edited[r.id]?.elec, w = edited[r.id]?.water;
      const rec: { elec?: number; water?: number } = {};
      if (e !== '' && e != null && !isNaN(Number(e))) rec.elec = Number(e);
      if (w !== '' && w != null && !isNaN(Number(w))) rec.water = Number(w);
      if (rec.elec != null || rec.water != null) monthReadings[r.id] = rec;
    }
    await save({ readings: { ...(dorm.readings ?? {}), [mk]: monthReadings } });
    setBusy(false);
  };

  return (
    <div style={{ padding: '4px 2px' }}>
      <Header plant={plant} title={tr('meter')} lang={lang} right={<Btn variant="primary" onClick={onSave}>{busy ? tr('saving') : tr('saveReading')}</Btn>} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 13, padding: '11px 15px', borderRadius: 12, fontSize: 12, color: C.text2, background: 'color-mix(in srgb, var(--blue) 9%, transparent)', border: '1px solid color-mix(in srgb, var(--blue) 30%, transparent)' }}>
        ⚡ {tr('readHint')}
      </div>
      <Card title={`${tr('unitsThisMonth')} · ${monthLabel(lang)}`} extra={<span style={{ fontSize: 11.5, color: C.muted }}>{tr('usageEq')}</span>}>
        {rooms.length === 0 ? <Hint text={tr('noRooms')} /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
              <thead><tr><th style={thCss}>{tr('roomNo')}</th><th style={thCss}>{tr('source')}</th><th style={{ ...thCss, textAlign: 'right' }}>{tr('elecUnits')}</th><th style={{ ...thCss, textAlign: 'right' }}>{tr('waterUnits')}</th><th style={thCss} /></tr></thead>
              <tbody>
                {rooms.map((r) => {
                  const iotAuto = r.meterSn && saved[r.id]?.elec == null && usage[r.id]?.units != null;
                  return (
                    <tr key={r.id}>
                      <td style={{ ...tdCss, fontWeight: 800, color: C.text }}>{r.no}</td>
                      <td style={tdCss}>{r.meterSn ? <Tag c={C.blue}>⚡ IoT auto</Tag> : <Tag c={C.amber}>✎ {tr('manual')}</Tag>}{usage[r.id]?.anomaly ? <> <Tag c={C.red}>{tr('anomaly')}</Tag></> : ''}</td>
                      <td style={{ ...tdCss, textAlign: 'right' }}>
                        <input style={numInput} type="number" value={edited[r.id]?.elec ?? ''} onChange={(e) => setVal(r.id, 'elec', e.target.value)} placeholder={r.meterSn ? '…' : '0'} />
                        {iotAuto ? <div style={{ fontSize: 9, color: C.blue, marginTop: 2 }}>{tr('autoFilled')}</div> : null}
                      </td>
                      <td style={{ ...tdCss, textAlign: 'right' }}><input style={numInput} type="number" value={edited[r.id]?.water ?? ''} onChange={(e) => setVal(r.id, 'water', e.target.value)} placeholder="0" /></td>
                      <td style={tdCss} />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ════════════════════════════ BILL ════════════════════════════
export function DormBillPage() {
  const { plant, dorm, rooms, tenants, rates, lang, tr, save } = useDorm();
  const usage = useMonthlyUsage(rooms);
  const mk = monthKey();
  const [rateEdit, setRateEdit] = useState(false);
  const [rr, setRr] = useState({ elecRate: String(rates.elecRate), waterRate: String(rates.waterRate), commonFee: String(rates.commonFee), dueDay: String(dorm.dueDay ?? 5) });
  const [busy, setBusy] = useState(false);
  if (!plant) return <Empty text={tr('selectDorm')} />;

  const dueDay = dorm.dueDay ?? 5;
  const overdueNow = new Date().getDate() > dueDay;
  const dueStr = `${dueDay} ${monthLabel(lang)}`;
  const reads = dorm.readings?.[mk] ?? {};
  const pays = dorm.payments?.[mk] ?? {};
  const tenantByRoom = new Map(tenants.filter((t) => t.roomId).map((t) => [t.roomId!, t]));
  const billed = rooms.filter((r) => tenantByRoom.has(r.id)).map((r) => {
    const elecU = reads[r.id]?.elec ?? (r.meterSn ? Math.round(usage[r.id]?.units ?? 0) : 0);
    const waterU = reads[r.id]?.water ?? 0;
    const elec = elecU * rates.elecRate, water = waterU * rates.waterRate;
    const paid = !!pays[r.id]?.paid;
    return { r, tenant: tenantByRoom.get(r.id)!, elecU, waterU, elec, water, total: (r.rent ?? 0) + elec + water + rates.commonFee, paid, overdue: !paid && overdueNow };
  });
  const toLine = (b: (typeof billed)[number]): BillLine => ({ room: b.r.no, tenant: b.tenant.name, rent: b.r.rent ?? 0, elec: b.elec, elecU: b.elecU, water: b.water, waterU: b.waterU, total: b.total });
  const grand = billed.reduce((s, b) => s + b.total, 0);
  const collected = billed.filter((b) => b.paid).reduce((s, b) => s + b.total, 0);
  const outstanding = grand - collected;

  const togglePaid = async (roomId: string, paid: boolean) => {
    const monthPays = { ...(dorm.payments?.[mk] ?? {}) };
    monthPays[roomId] = { paid, paidAt: paid ? new Date().toISOString() : undefined };
    await save({ payments: { ...(dorm.payments ?? {}), [mk]: monthPays } });
  };
  const saveRates = async () => {
    setBusy(true);
    await save({ elecRate: Number(rr.elecRate) || 0, waterRate: Number(rr.waterRate) || 0, commonFee: Number(rr.commonFee) || 0, dueDay: Number(rr.dueDay) || 5 });
    setBusy(false); setRateEdit(false);
  };

  return (
    <div style={{ padding: '4px 2px' }}>
      <Header plant={plant} title={tr('billing')} lang={lang} right={<Btn variant="primary" onClick={() => printBills(billed.map(toLine), plant.name, rates, monthLabel(lang), dueStr, lang)}>{tr('printAll')}</Btn>} />

      {/* rates row + edit */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: C.muted }}>
          {tr('ratePrefix')} ฿{rates.elecRate}{tr('perUnit')} · {tr('water')} ฿{rates.waterRate}{tr('perUnit')} · {tr('common')} ฿{rates.commonFee}
        </span>
        <Btn small onClick={() => { setRr({ elecRate: String(rates.elecRate), waterRate: String(rates.waterRate), commonFee: String(rates.commonFee), dueDay: String(dueDay) }); setRateEdit((v) => !v); }}>{tr('editRates')}</Btn>
      </div>
      {rateEdit && (
        <div style={{ ...card, padding: 15, marginBottom: 14, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <L label={tr('elecRateL')}><input style={{ ...numInput, width: 110 }} type="number" value={rr.elecRate} onChange={(e) => setRr({ ...rr, elecRate: e.target.value })} /></L>
          <L label={tr('waterRateL')}><input style={{ ...numInput, width: 110 }} type="number" value={rr.waterRate} onChange={(e) => setRr({ ...rr, waterRate: e.target.value })} /></L>
          <L label={tr('commonFeeL')}><input style={{ ...numInput, width: 110 }} type="number" value={rr.commonFee} onChange={(e) => setRr({ ...rr, commonFee: e.target.value })} /></L>
          <L label={tr('dueDay')}><input style={{ ...numInput, width: 80 }} type="number" min={1} max={28} value={rr.dueDay} onChange={(e) => setRr({ ...rr, dueDay: e.target.value })} /></L>
          <Btn variant="primary" onClick={saveRates}>{busy ? tr('saving') : tr('saveRates')}</Btn>
        </div>
      )}

      {/* collected / outstanding summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px,1fr))', gap: 12, marginBottom: 14 }}>
        {[{ lab: tr('total'), val: money(grand), c: C.accent }, { lab: tr('paidSum'), val: money(collected), c: C.green }, { lab: tr('unpaidSum'), val: money(outstanding), c: C.red }].map((k) => (
          <div key={k.lab} style={{ ...card, padding: '13px 16px', position: 'relative', background: `linear-gradient(135deg, color-mix(in srgb, ${k.c} 8%, var(--bg-surface)), var(--bg-surface))` }}>
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: k.c }} />
            <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: C.muted }}>{k.lab}</div>
            <div style={{ marginTop: 6, fontSize: 22, fontWeight: 800, color: C.text }}>{k.val}</div>
          </div>
        ))}
      </div>

      <Card title={`${tr('monthlyBills')} · ${monthLabel(lang)}`}>
        {billed.length === 0 ? <Hint text={tr('noBillRooms')} /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
              <thead><tr><th style={thCss}>{tr('roomNo')}</th><th style={thCss}>{tr('colTenant')}</th><th style={{ ...thCss, textAlign: 'right' }}>{tr('colRent')}</th><th style={{ ...thCss, textAlign: 'right' }}>{tr('elecCost')}</th><th style={{ ...thCss, textAlign: 'right' }}>{tr('waterCol')}</th><th style={{ ...thCss, textAlign: 'right' }}>{tr('common')}</th><th style={{ ...thCss, textAlign: 'right' }}>{tr('grand')}</th><th style={{ ...thCss, textAlign: 'center' }}>{tr('status')}</th><th style={{ ...thCss, textAlign: 'right' }} /></tr></thead>
              <tbody>
                {billed.map((b) => (
                  <tr key={b.r.id}>
                    <td style={{ ...tdCss, fontWeight: 800, color: C.text }}>{b.r.no}</td>
                    <td style={tdCss}>{b.tenant.name}</td>
                    <td style={{ ...tdCss, textAlign: 'right' }}>{money(b.r.rent ?? 0)}</td>
                    <td style={{ ...tdCss, textAlign: 'right' }}>{money(b.elec)}<span style={{ fontSize: 9.5, color: C.muted }}> ({b.elecU})</span></td>
                    <td style={{ ...tdCss, textAlign: 'right' }}>{money(b.water)}<span style={{ fontSize: 9.5, color: C.muted }}> ({b.waterU})</span></td>
                    <td style={{ ...tdCss, textAlign: 'right' }}>{money(rates.commonFee)}</td>
                    <td style={{ ...tdCss, textAlign: 'right', fontWeight: 800, color: C.text }}>{money(b.total)}</td>
                    <td style={{ ...tdCss, textAlign: 'center' }}>
                      {b.paid
                        ? <span style={{ cursor: 'pointer' }} onClick={() => togglePaid(b.r.id, false)} title={tr('markUnpaid')}><Tag c={C.green}>✓ {tr('paid')}</Tag></span>
                        : (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {b.overdue && <Tag c={C.red}>{tr('overdue')}</Tag>}
                            <Btn small variant="primary" onClick={() => togglePaid(b.r.id, true)}>{tr('markPaid')}</Btn>
                          </div>
                        )}
                    </td>
                    <td style={{ ...tdCss, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Btn small onClick={() => printBills([toLine(b)], plant.name, rates, monthLabel(lang), dueStr, lang)}>🖨</Btn>{' '}
                      <Btn small onClick={() => lineShareBill(toLine(b), plant.name, monthLabel(lang), rates, lang)}>📤 LINE</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <div style={{ fontSize: 10.5, color: C.muted, marginTop: 9 }}>{tr('billFoot')}</div>
    </div>
  );
}
